import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { membershipPackageService } from '../../services';
import { formatCurrency } from '../../utils/helpers';

const ACTIVE_STATUS = 'Hoạt Động';
const INACTIVE_STATUS = 'Ngừng Hoạt Động';
const currencyFormatter = new Intl.NumberFormat('vi-VN', {
  maximumFractionDigits: 0
});

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

const MembershipPackageList = () => {
  const [form] = Form.useForm();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(null);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const result = await membershipPackageService.getAll({
        search,
        trangThai: status,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });

      if (!result?.success || !Array.isArray(result.data)) {
        throw new Error(result?.message || 'Dữ liệu danh sách gói không hợp lệ');
      }

      const rawTotal = result.pagination?.total;
      setPackages(result.data);
      setTotal(
        rawTotal !== null && rawTotal !== undefined && Number.isFinite(Number(rawTotal))
          ? Number(rawTotal)
          : null
      );
    } catch (error) {
      setPackages([]);
      setTotal(null);
      message.error(getErrorMessage(error, 'Không thể tải danh sách gói thành viên'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search, status]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const openCreateModal = () => {
    setEditingPackage(null);
    form.resetFields();
    form.setFieldsValue({
      tenGoi: '',
      gia: 0,
      thoiHan: 1,
      quyenLoi: '',
      trangThai: ACTIVE_STATUS
    });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingPackage(record);
    form.resetFields();
    form.setFieldsValue({
      tenGoi: record.TenGoi ?? record.tenGoi ?? '',
      gia: Number(record.Gia ?? record.gia ?? 0),
      thoiHan: Number(
        record.ThoiHanThang
        ?? record.ThoiHan
        ?? record.thoiHan
        ?? 1
      ),
      quyenLoi: record.QuyenLoi ?? record.quyenLoi ?? '',
      trangThai: record.TrangThai ?? record.trangThai ?? ACTIVE_STATUS
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingPackage(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        tenGoi: values.tenGoi.trim(),
        gia: Number(values.gia),
        thoiHan: Number(values.thoiHan),
        quyenLoi: values.quyenLoi.trim(),
        trangThai: values.trangThai.trim()
      };

      setSaving(true);
      const result = editingPackage
        ? await membershipPackageService.update(editingPackage.id, payload)
        : await membershipPackageService.create(payload);

      if (result?.success === false) {
        throw new Error(result.message || 'Không thể lưu gói thành viên');
      }

      message.success(result?.message || (
        editingPackage
          ? 'Cập nhật gói thành viên thành công'
          : 'Thêm gói thành viên thành công'
      ));
      setModalOpen(false);
      setEditingPackage(null);
      form.resetFields();

      if (!editingPackage && currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchPackages();
      }
    } catch (error) {
      if (!error?.errorFields) {
        message.error(getErrorMessage(error, 'Không thể lưu gói thành viên'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (record, nextStatus) => {
    if (nextStatus === record.TrangThai) return;

    setUpdatingStatusId(record.id);
    try {
      const result = await membershipPackageService.updateStatus(record.id, nextStatus);
      if (result?.success === false) {
        throw new Error(result.message || 'Không thể cập nhật trạng thái');
      }

      setPackages((current) => current.map((item) => (
        item.id === record.id ? { ...item, TrangThai: nextStatus } : item
      )));
      message.success(result?.message || 'Cập nhật trạng thái thành công');
    } catch (error) {
      message.error(getErrorMessage(error, 'Không thể cập nhật trạng thái'));
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDelete = async (record) => {
    try {
      const result = await membershipPackageService.delete(record.id);
      if (result?.success === false) {
        throw new Error(result.message || 'Không thể xóa gói thành viên');
      }

      message.success(result?.message || 'Xóa gói thành viên thành công');
      if (packages.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        fetchPackages();
      }
    } catch (error) {
      message.error(getErrorMessage(error, 'Không thể xóa gói thành viên'));
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setStatus(undefined);
    setCurrentPage(1);
  };

  const estimatedTotal = total ?? (
    (currentPage - 1) * pageSize + packages.length + (packages.length === pageSize ? 1 : 0)
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 75,
      fixed: 'left'
    },
    {
      title: 'Tên gói',
      dataIndex: 'TenGoi',
      width: 220
    },
    {
      title: 'Giá',
      dataIndex: 'Gia',
      width: 150,
      align: 'right',
      render: (value) => formatCurrency(Number(value || 0))
    },
    {
      title: 'Thời hạn',
      dataIndex: 'ThoiHanThang',
      width: 115,
      render: (value) => `${value} tháng`
    },
    {
      title: 'Quyền lợi',
      dataIndex: 'QuyenLoi',
      ellipsis: true,
      width: 320
    },
    {
      title: 'Trạng thái',
      dataIndex: 'TrangThai',
      width: 210,
      render: (value, record) => (
        <Select
          value={value}
          loading={updatingStatusId === record.id}
          disabled={updatingStatusId !== null}
          onChange={(nextStatus) => handleStatusChange(record, nextStatus)}
          style={{ width: 185 }}
          options={[
            { value: ACTIVE_STATUS, label: <Tag color="green">{ACTIVE_STATUS}</Tag> },
            { value: INACTIVE_STATUS, label: <Tag color="default">{INACTIVE_STATUS}</Tag> },
            ...(![ACTIVE_STATUS, INACTIVE_STATUS].includes(value)
              ? [{ value, label: value }]
              : [])
          ]}
        />
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Xóa gói thành viên"
            description={`Bạn có chắc muốn xóa “${record.TenGoi}”?`}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="Quản lý gói thành viên"
        extra={(
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Thêm gói
          </Button>
        )}
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            value={searchInput}
            prefix={<SearchOutlined />}
            placeholder="Tìm tên, quyền lợi, giá, thời hạn..."
            style={{ width: 320 }}
            onChange={(event) => setSearchInput(event.target.value)}
            onPressEnter={handleSearch}
          />
          <Select
            allowClear
            value={status}
            placeholder="Tất cả trạng thái"
            style={{ width: 200 }}
            options={[
              { value: ACTIVE_STATUS, label: ACTIVE_STATUS },
              { value: INACTIVE_STATUS, label: INACTIVE_STATUS }
            ]}
            onChange={(value) => {
              setStatus(value);
              setCurrentPage(1);
            }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Tìm kiếm
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Đặt lại
          </Button>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={packages}
          loading={loading}
          scroll={{ x: 1220 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: estimatedTotal,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: total === null
              ? () => `Trang ${currentPage} · API chưa trả tổng số bản ghi`
              : (value) => `Tổng ${value} gói`,
            onChange: (page, nextPageSize) => {
              if (nextPageSize !== pageSize) {
                setPageSize(nextPageSize);
                setCurrentPage(1);
              } else {
                setCurrentPage(page);
              }
            }
          }}
        />
      </Card>

      <Modal
        title={editingPackage ? 'Cập nhật gói thành viên' : 'Thêm gói thành viên'}
        open={modalOpen}
        okText={editingPackage ? 'Cập nhật' : 'Thêm gói'}
        cancelText="Hủy"
        confirmLoading={saving}
        onOk={handleSave}
        onCancel={closeModal}
        forceRender
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="tenGoi"
            label="Tên gói"
            rules={[
              { required: true, whitespace: true, message: 'Vui lòng nhập tên gói' },
              { max: 255, message: 'Tên gói tối đa 255 ký tự' }
            ]}
          >
            <Input placeholder="Ví dụ: Gói VIP 12 tháng" maxLength={255} showCount />
          </Form.Item>

          <Space size="large" align="start" style={{ display: 'flex' }}>
            <Form.Item
              name="gia"
              label="Giá"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
            >
              <InputNumber
                min={0}
                precision={0}
                controls={false}
                style={{ width: '100%' }}
                placeholder="0"
                addonAfter="đ"
                formatter={(value) => (
                  value === undefined || value === null || value === ''
                    ? ''
                    : currencyFormatter.format(Number(value))
                )}
                parser={(value) => value?.replace(/[^0-9]/g, '') || '0'}
              />
            </Form.Item>
            <Form.Item
              name="thoiHan"
              label="Thời hạn"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Vui lòng nhập thời hạn' }]}
            >
              <InputNumber
                min={1}
                precision={0}
                style={{ width: '100%' }}
                placeholder="12"
                addonAfter="tháng"
              />
            </Form.Item>
          </Space>

          <Form.Item
            name="quyenLoi"
            label="Quyền lợi"
            rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập quyền lợi' }]}
          >
            <Input.TextArea rows={4} placeholder="Mô tả quyền lợi của gói" />
          </Form.Item>

          <Form.Item
            name="trangThai"
            label="Trạng thái"
            rules={[
              { required: true, whitespace: true, message: 'Vui lòng chọn trạng thái' },
              { max: 20, message: 'Trạng thái tối đa 20 ký tự' }
            ]}
          >
            <Select
              options={[
                { value: ACTIVE_STATUS, label: ACTIVE_STATUS },
                { value: INACTIVE_STATUS, label: INACTIVE_STATUS }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MembershipPackageList;
